import React, { createContext, useCallback, useContext, useMemo, useState } from "react";

export type ToastType = "success" | "error" | "info" | "warning";

export type Toast = {
  id: string;
  title?: string;
  message: string;
  type?: ToastType;
  durationMs?: number;
};

type ToastContextValue = {
  showToast: (toast: Omit<Toast, "id">) => void;
};

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const showToast = useCallback((toast: Omit<Toast, "id">) => {
    const id = Math.random().toString(36).slice(2);
    const full: Toast = { id, durationMs: 3000, type: "info", ...toast };
    setToasts((prev) => [...prev, full]);

    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, full.durationMs);
  }, []);

  const value = useMemo(() => ({ showToast }), [showToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {/* Toast container visual */}
      <div className="fixed z-[100005] right-4 flex flex-col gap-2" style={{ bottom: 'calc(env(safe-area-inset-bottom) + 1rem)' }}>
        {toasts.map((t) => {
          const color =
            t.type === "success"
              ? "bg-green-600"
              : t.type === "error"
              ? "bg-red-600"
              : t.type === "warning"
              ? "bg-yellow-600"
              : "bg-gray-800";

          return (
            <div
              key={t.id}
              className={`${color} text-white shadow-theme-md rounded-lg px-4 py-3 min-w-[260px] max-w-[360px]`}
            >
              {t.title && <div className="mb-0.5 font-semibold">{t.title}</div>}
              <div className="text-sm leading-5">{t.message}</div>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within a ToastProvider");
  return ctx;
}
